import { useEffect } from "react";
import { ArrowLeft, House } from "lucide-react";
import { useNavigate, useRouteError } from "react-router-dom";
import { Button } from "../components/nuclear/button";
import "./ErrorPage.module.css";

const defaultStatusText = {
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  405: "Method Not Allowed",
  408: "Request Timeout",
  409: "Conflict",
  422: "Unprocessable Entity",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

function getErrorDetails(error) {
  if (!error) {
    return "";
  }
  if (typeof error?.data === "string") {
    return error.data;
  }
  if (typeof error?.data?.message === "string") {
    return error.data.message;
  }
  if (typeof error?.payload?.message === "string") {
    return error.payload.message;
  }
  if (typeof error?.message === "string") {
    return error.message;
  }
  return "";
}

function getErrorTitle({ status, isNotFound, error }) {
  if (isNotFound) {
    return "Page Not Found";
  }
  if (typeof error?.statusText === "string" && error.statusText.trim().length) {
    return error.statusText;
  }
  return defaultStatusText[status] || "Unexpected Error";
}

export default function ErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const hasStatus = typeof error?.status === "number";
  const status = hasStatus ? error.status : 500;
  const isNotFound = status === 404;
  const details = getErrorDetails(error);
  const title = getErrorTitle({ status, isNotFound, error });
  const description = isNotFound
    ? "Oops! It seems you've drifted into uncharted territory. The page you're looking for doesn't exist or has been moved."
    : "We hit a problem while loading this page. You can go back or return to home.";

  useEffect(() => {
    document.title = `${status} - ${title} | Animotive VR Dashboard`;
  }, [status, title]);

  function handleGoBack() {
    window.history.back();
  }

  function handleBackToHome() {
    navigate("/");
  }

  return (
    <div className="error-page-shell" role="main">
      <div className="error-page-container">
        <div className="error-page-illustration">
          <img
            src={"/img/DownloadPage/ErrorPageScary.png"}
            alt="Error illustration showing a character in distress"
            className="error-page-image"
          />
        </div>

        <div className="error-page-content" data-testid="fallback-route">
          <div className="error-page-badge">Error {status}</div>
          <h1 className="error-page-title">{title}</h1>
          <p className="error-page-description">{description}</p>
          {!isNotFound && details ? (
            <p className="error-page-details">{details}</p>
          ) : null}

          <div className="error-page-actions">
            <Button
              type="button"
              variant="ghost"
              className="error-page-action-button error-page-action-button--secondary"
              onClick={handleGoBack}
              aria-label="Go back to previous page"
            >
              <ArrowLeft size={20} aria-hidden="true" />
              Go Back
            </Button>

            <Button
              type="button"
              className="error-page-action-button error-page-action-button--primary"
              onClick={handleBackToHome}
              aria-label="Return to dashboard home"
            >
              <House size={20} aria-hidden="true" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
